"use client"

import { useState, useEffect } from "react"
import { Button } from "./button.jsx"
import { Input } from "./input.jsx"
import { Label } from "./label.jsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select.jsx"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./table.jsx"
import { Plus, Trash2 } from "lucide-react"

export function SpreadsheetEditor({
    propsColumns,
    data,
    onChange,
    xAxisField,
    yAxisField,
    onAxisChange,
    readOnly = false,
    onColumnsChange = () => { }
}) {
    const [columns, setColumns] = useState(() => {
        if (propsColumns && propsColumns.length > 0) {
            return propsColumns
        }
        if (data.length === 0) return ["name", "value"]
        return Object.keys(data[0])
    })

    const [editingColumnNames, setEditingColumnNames] = useState({})

    const addRow = () => {
        if (readOnly) return
        const newRow = {}
        columns.forEach((col) => {
            newRow[col] = col === "value" ? 0 : ""
        })
        onChange([...data, newRow])
    }

    const removeRow = (index) => {
        if (readOnly) return
        const newData = data.filter((_, i) => i !== index)
        onChange(newData)
    }

    const addColumn = () => {
        if (readOnly) return
        const newColumnName = `欄位${columns.length + 1}`
        const newColumns = [...columns, newColumnName]
        setColumns(newColumns)
        onColumnsChange(newColumns)


        const newData = data.map((row) => ({
            ...row,
            [newColumnName]: "",
        }))
        onChange(newData)
    }

    const removeColumn = (columnIndex) => {
        if (readOnly) return
        const columnToRemove = columns[columnIndex]
        const newColumns = columns.filter((_, i) => i !== columnIndex)
        setColumns(newColumns)
        onColumnsChange(newColumns)

        const newData = data.map((row) => {
            const { [columnToRemove]: removed, ...rest } = row
            return rest
        })
        onChange(newData)

        // Update axis fields if they reference the removed column
        if (xAxisField === columnToRemove) onAxisChange("", yAxisField)
        if (yAxisField === columnToRemove) onAxisChange(xAxisField, "")
    }

    const updateCell = (rowIndex, column, value) => {
        if (readOnly) return
        const newData = [...data]
        newData[rowIndex] = {
            ...newData[rowIndex],
            [column]: column === "value" || !isNaN(Number(value)) ? Number(value) || value : value,
        }
        onChange(newData)
    }

    const updateColumnName = (oldName, newName) => {
        if (readOnly) return
        if (oldName === newName || !newName.trim()) return

        const newColumns = columns.map((col) => (col === oldName ? newName : col))
        setColumns(newColumns)
        onColumnsChange(newColumns)


        const newData = data.map((row) => {
            const { [oldName]: value, ...rest } = row
            return { ...rest, [newName]: value }
        })
        onChange(newData)

        // Update axis fields if they reference the renamed column
        if (xAxisField === oldName) onAxisChange(newName, yAxisField)
        if (yAxisField === oldName) onAxisChange(xAxisField, newName)
    }

    const handleColumnNameChange = (column, value) => {
        setEditingColumnNames((prev) => ({ ...prev, [column]: value }))
    }

    const handleColumnNameBlur = (column) => {
        const newName = editingColumnNames[column]
        if (newName !== undefined && newName !== column) {
            updateColumnName(column, newName.trim())
        }
        // Clear the editing state
        setEditingColumnNames((prev) => {
            const { [column]: removed, ...rest } = prev
            return rest
        })
    }

    const handleColumnNameKeyDown = (e, column) => {
        if (e.key === "Enter") {
            e.currentTarget.blur()
        }
    }
    useEffect(() => {
        if (propsColumns && propsColumns.length > 0) {
            setColumns(propsColumns)
        }
    }, [propsColumns])
    
    return (
        <div className="space-y-4">
            {/* Axis Configuration */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>X軸欄位</Label>
                    <Select value={xAxisField} onValueChange={(value) => onAxisChange(value, yAxisField)}>
                        <SelectTrigger>
                            <SelectValue placeholder="選擇X軸欄位" />
                        </SelectTrigger>
                        <SelectContent>
                            {columns.map((col) => (
                                <SelectItem key={col} value={col}>
                                    {col}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Y軸欄位</Label>
                    <Select value={yAxisField} onValueChange={(value) => onAxisChange(xAxisField, value)}>
                        <SelectTrigger>
                            <SelectValue placeholder="選擇Y軸欄位" />
                        </SelectTrigger>
                        <SelectContent>
                            {columns.map((col) => (
                                <SelectItem key={col} value={col}>
                                    {col}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Spreadsheet Table */}
            <div className="border rounded-lg">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column, index) => (
                                <TableHead key={column} className="min-w-[120px]">
                                    <div className="flex items-center gap-2">
                                        {readOnly ? (
                                            <div className="h-8 flex items-center text-sm font-medium">{editingColumnNames[column] ?? column}</div>
                                        ) : (
                                            <>
                                                <Input
                                                    value={editingColumnNames[column] ?? column}
                                                    onChange={(e) => handleColumnNameChange(column, e.target.value)}
                                                    onBlur={() => handleColumnNameBlur(column)}
                                                    onKeyDown={(e) => handleColumnNameKeyDown(e, column)}
                                                    className="h-8 text-sm font-medium"
                                                    placeholder="欄位名稱"
                                                />
                                                {columns.length > 2 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeColumn(index)}
                                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </TableHead>
                            ))}
                            {!readOnly && (
                                <TableHead className="w-12">
                                    <Button variant="ghost" size="sm" onClick={addColumn} className="h-8 w-8 p-0">
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {data.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {columns.map((column) => (
                                    <TableCell key={column}>
                                        <Input
                                            value={row[column] || ""}
                                            onChange={(e) => updateCell(rowIndex, column, e.target.value)}
                                            className="h-8 border-0 focus-visible:ring-1"
                                            placeholder={column === "value" ? "0" : "輸入數據"}
                                            disabled={readOnly}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell>
                                    {!readOnly && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeRow(rowIndex)}
                                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Add Row Button */}
            {!readOnly && (
                <Button onClick={addRow} variant="outline" className="w-full bg-transparent">
                    <Plus className="h-4 w-4 mr-2" />
                    新增行
                </Button>
            )}
        </div>
    )
}
